import { useState, useCallback } from "react";

// import errorUtils from "utils/error-utils";
import SelectCategory from "components/forms/DynamicSelectBoxCategory";
import BooleanDropdown from "components/forms/BooleanDropdown";
import { stringToBoolean } from "utils/boolean-checker";
import DialogButtonDivStyled from "components/elements/DialogButtonDivStyled";
import DialogButtonStyled from "components/elements/DialogButtonStyled";
import FormStyled from "components/forms/FormStyled";
import FormStyledError from "components/forms/FormStyledError";
import { PersonCreateDto } from "@kdmanalytics/brm-system";
import PropTypes from "prop-types";
import { projectIdState } from "atoms/atoms-admin";
import { useRecoilValue } from "recoil";
import TextField from "components/forms/TextField";
import { FORM_PLACEHOLDER, FORM_LABEL, FORM_ERROR } from "constants/brm";
import SelectAll from "components/forms/SelectAllReactSelect";
import { sortUpperCase } from "utils/sorting";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner as Loading } from "components/elements";
import { SystemApi } from "features/brm";
import { validateNameUniqueness } from "features/system-model";

const AddPersonForm = ({ setModalIsOpen }) => {
  const projectId = useRecoilValue(projectIdState);

  const queryClient = useQueryClient();

  const { data: persons, isError: isPersonsError } = SystemApi.usePersons({
    projectId,
    options: { enabled: !!projectId },
  });

  const {
    data: elementCategories,
    isError: isCatError,
    error: catError,
  } = SystemApi.useElementCategories({
    projectId,
    options: {
      select: useCallback((data) => data.sort(sortUpperCase), []),
    },
  });

  const {
    data: nodes,
    isError: isNodeError,
    error: nodeError,
  } = SystemApi.useNodes({
    projectId,
    options: {
      select: useCallback((data) => data.map((d) => ({ value: d.id, label: d.name })), []),
    },
  });

  const {
    data: activities,
    isError: isActivitiesError,
    error: activitiesError,
  } = SystemApi.useActivities({
    projectId,
    options: {
      select: useCallback((data) => data.map((d) => ({ value: d.id, label: d.name })), []),
    },
  });

  const createPerson = SystemApi.useCreatePerson({
    config: {
      onSuccess: async () => {
        queryClient.invalidateQueries(["persons", projectId]);
      },
    },
  });
  const addPersonNode = SystemApi.useAddPersonNode();
  const addPersonActivity = SystemApi.useAddPersonActivity();

  const [postError, setPostError] = useState("");
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [isTextValid, setIsTextValid] = useState(true);

  function validate(formData) {
    let error = true;
    if (
      formData.name.value === "" ||
      formData.category.value === "Select Category" ||
      formData.isInternal.value === null
    ) {
      error = false;
    }
    return error;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const isNameValid = validateNameUniqueness(persons, e.target.elements.name.value);
    if (!isNameValid) {
      setPostError(FORM_ERROR.duplicatePersonName);
      return null;
    }

    const isValid = validate(e.target.elements);

    if (isValid && isTextValid) {
      const params = {
        name: e.target.elements.name.value,
        note: e.target.elements.note.value,
        category: e.target.elements.category.value,
        isInternal: stringToBoolean(e.target.elements.isInternal.value),
      };

      const dto = PersonCreateDto.constructFromObject(params);
      try {
        const personId = await createPerson.mutateAsync({ projectId, personCreateDto: dto });

        await Promise.all(
          selectedNodes.map((n) => {
            return addPersonNode.mutateAsync({ personId, nodeId: n.value });
          })
        );

        await Promise.all(
          selectedActivities.map((a) => {
            return addPersonActivity.mutateAsync({ personId, activityId: a.value });
          })
        );
        setModalIsOpen(false);
      } catch (error) {
        console.error(error);
      }
    } else {
      return isTextValid ? setPostError(FORM_ERROR.missingFields) : setPostError(FORM_ERROR.invalidCharacters);
    }
    return null;
  }

  const handleActivitySelectChange = (selectedItems) => {
    const items = selectedItems || [];
    setSelectedActivities(items);
  };

  const handleNodeSelectChange = (selectedItems) => {
    const items = selectedItems || [];
    setSelectedNodes(items);
  };

  const isError = isActivitiesError || isCatError || isNodeError || isPersonsError;
  if (isError) {
    let errorType = "Unknown";
    let error = null;
    if (isActivitiesError) {
      errorType = "Activities";
      error = activitiesError;
    } else if (isCatError) {
      errorType = "Categories";
      error = catError;
    } else if (isNodeError) {
      errorType = "Nodes";
      error = nodeError;
    }
    return (
      <>
        Error loading {errorType} : {error}
      </>
    );
  }

  if (elementCategories && nodes && activities) {
    return (
      <div>
        <form onSubmit={handleSubmit} action="">
          <FormStyled>
            <div className="form-style">
              <TextField
                label={{ id: "AddPersonForm_name", name: FORM_LABEL.nameMandatory }}
                input={{ name: "name", placeholder: FORM_PLACEHOLDER.name }}
                setIsTextValid={setIsTextValid}
              />
              <TextField
                label={{ id: "AddPersonForm_note", name: FORM_LABEL.note }}
                input={{ name: "note", placeholder: FORM_PLACEHOLDER.note }}
                setIsTextValid={setIsTextValid}
              />

              <BooleanDropdown
                label={{ id: "AddPersonForm_isInternal", name: FORM_LABEL.internalMandatory }}
                select={{ id: "isInternal", name: "isInternal" }}
              />

              <label id="AddPersonForm_category">{FORM_LABEL.categoryMandatory}</label>
              <SelectCategory
                id="AddPersonForm_categoryDropdown"
                arrayOfData={elementCategories}
                name="Category"
                item="category"
              />
              <label id="AddPersonForm_node">{FORM_LABEL.nodesOptional}</label>
              <SelectAll
                isMulti
                options={nodes}
                defaultValue={selectedNodes}
                value={selectedNodes}
                onChange={handleNodeSelectChange}
                allowSelectAll
                allOption={{
                  label: "Select all",
                  value: "*",
                }}
                elementId="AddPersonForm_node"
              />

              <br />
              <label id="AddPersonForm_activity">{FORM_LABEL.activitiesOptional}</label>
              <SelectAll
                isMulti
                options={activities}
                defaultValue={selectedActivities}
                value={selectedActivities}
                onChange={handleActivitySelectChange}
                allowSelectAll
                allOption={{
                  label: "Select all",
                  value: "*",
                }}
                elementId="AddPersonForm_activity"
              />
            </div>
          </FormStyled>
          <FormStyledError>{postError}</FormStyledError>
          <DialogButtonDivStyled>
            <DialogButtonStyled onClick={() => setModalIsOpen(false)} id="AddPersonForm_cancelButton">
              Cancel
            </DialogButtonStyled>
            <DialogButtonStyled type="submit" id="AddPersonForm_addButton">
              Add
            </DialogButtonStyled>
          </DialogButtonDivStyled>
        </form>
      </div>
    );
  }
  return <Loading />;
};

AddPersonForm.propTypes = {
  setModalIsOpen: PropTypes.func.isRequired,
};

export default AddPersonForm;
